import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { useSelector } from 'react-redux'
import FormItem from './FormItem'
import { Divider, Input, Radio, Select, Checkbox } from 'antd'
import { faAddressBook, faBan, faClock, faCog, faComment, faCompass, faIdBadge, faNotesMedical, faPhone, faRecycle, faSave, faUserTie } from '@fortawesome/pro-solid-svg-icons'
import { isValid, ValidationFields } from '../../../utils/validators'
import IconButton from '../../antd/Buttons/IconButton'
import { SCHEDULES, SECTOR_VALIDATORS, TYPES } from '../../../utils/entities/sector'
import { isFunction, isArray } from 'lodash'
import { handleFilter } from '../../antd/antdHelper'
import UsersHeap from '../../UsersHeap'
import LabelInputNumber from '../../antd/Inputs/LabelInputNumber'
import StudentPathwaysBuffer from '../StudentPathwaysBuffer'
import { selectUser } from '../../../reducers/UserReducer'
import roles, { isHospitalAdmin, isObserver } from '../../../utils/roles'
import { actType } from '../../../utils/entities/actType'
import { selectTranslate } from '../../../reducers/LocaleReducer/LocalReducer'

const { Option } = Select
const { Group, Button } = Radio

const SectorForm = ({ actTypes = [], cancel, item, users = [], onCancel, onSubmit }) => {
  const t = useSelector(selectTranslate)
  const user = useSelector(selectUser)

  const [data, setData] = useState({ ...item, schedules: item.schedules || [], type: item.type || '' })
  const [error, setError] = useState({ missingFields: [] })

  const sectorActTypes = useMemo(() => item.actTypes ? data.actTypes.map(a => a.id) : [], [data.actTypes])
  const observers = useMemo(() => users.filter(isObserver), [users])
  const isNurse = useMemo(() => roles.isNurse(user), [user])

  const errorClass = useCallback(error.missingFields.reduce((acc, f) => {
    acc[f] = 'error-ant-border'

    return acc
  }, {}), [error])

  useEffect(() => {
    setData({ ...item, schedules: item.schedules || [], type: item.type || '' })
  }, [item, setData])

  const handleSubmit = useCallback(() => {
    const purgeData = { ...data, type: data.type === '' ? null : data.type }
    purgeData.actTypes = sectorActTypes.map(a => ({ id: a }))

    const errors = isValid(purgeData, SECTOR_VALIDATORS)

    if (errors.keys.length === 0) {
      onSubmit(purgeData)
    } else {
      const details = {}

      Object.keys(errors.messages).forEach(key => {
        details[t(key)] = errors.messages[key].map(m => t(m))
      })

      setError({
        ...error,
        title: t('form.errors'),
        details,
        visible: true,
        missingFields: errors.keys
      })
    }
  }, [data, error, setError, t])

  const schedules = useMemo(() => [
    ...Object.keys(SCHEDULES).map(k => (
      <Option key={k} value={SCHEDULES[k]}> {t(`sector_schedules.${k.toLowerCase()}`)} </Option>
    ))
  ], [t])
  const types = useMemo(() => [
    <Button key='empty' value=''> / </Button>,
    ...Object.keys(TYPES).map(k => (
      <Button key={k} value={TYPES[k]}> {t(`sector_types.${k.toLowerCase()}`)} </Button>
    ))
  ], [t])

  const sortActTypes = useMemo(() => actTypes.reduce((acc, a) => {
    if (isArray(a.sections)) {
      a.sections.forEach(s => acc.push({ ...a, section: s }))
    }

    return acc
  }, []).sort((a, b) => {
    if (a.section && b.section) {
      if (a.section.id === b.section.id) {
        return a.acronym.localeCompare(b.acronym)
      }

      return a.section.name.localeCompare(b.section.name)
    } else if (a.section) {
      return -1
    } else if (b.section) {
      return 1
    }

    return a.name.localeCompare(b.name)
  }), [actTypes])

  return (
    <div className='flex-col'>
      <div className='flex-row'>
        <div className='flex-col w-50'>
          <FormItem icon={faIdBadge} label='form.label.name' required>
            <Input className={errorClass[ValidationFields.NAME]} value={data.name} disabled={isNurse} onChange={e => setData({ ...data, name: e.target.value })} />
          </FormItem>
          <FormItem icon={faComment} label='form.label.specificity'>
            <Input.TextArea maxLength={500} rows={4} value={data.comment} onChange={e => setData({ ...data, comment: e.target.value })} />
          </FormItem>
          <FormItem icon={faCompass} label='form.label.emplacement'>
            <Input value={data.emplacement} onChange={e => setData({ ...data, emplacement: e.target.value })} />
          </FormItem>
          <FormItem icon={faPhone} label='form.label.phone_number'>
            <Input value={data.phoneNumber} onChange={e => setData({ ...data, phoneNumber: e.target.value })} />
          </FormItem>
          <FormItem icon={faUserTie} label='form.label.service_administrator'>
            <UsersHeap disabled={!isHospitalAdmin(user)} values={data.managers ?? []} users={users} onSelect={managers => setData({ ...data, managers })} />
          </FormItem>
          <FormItem style={{ margin: '0px' }} icon={faUserTie} label='form.label.internship_types'>
            <Select
              filterOption={handleFilter}
              mode='multiple'
              style={{ width: '100%' }}
              value={sectorActTypes}
              onChange={values => setData({ ...data, actTypes: values.map(v => ({ id: v })) })}
            >
              {sortActTypes.map(a => (<Option key={a.id} value={a.id} title={actType.getFullName(a)} filter={actType.getFullAcronym(a)}> {actType.getFullAcronym(a)} </Option>))}
            </Select>
          </FormItem>
        </div>
        <Divider style={{ height: 'auto' }} type='vertical' />
        <div className='flex-col w-50'>
          <FormItem icon={faAddressBook} label='form.label.service_tutors'>
            <UsersHeap disabled={!isHospitalAdmin(user)} values={data.tutors ?? []} users={observers} onSelect={tutors => setData({ ...data, tutors })} />
          </FormItem>
          <FormItem icon={faClock} label='form.label.schedule_type'>
            <Select
              mode='multiple'
              value={data.schedules}
              onChange={(value) => setData({ ...data, schedules: value })}
              style={{ width: '100%' }}
            >
              {schedules}
            </Select>
          </FormItem>
          <FormItem icon={faNotesMedical} label='form.label.type'>
            <Group value={data.type} onChange={e => setData({ ...data, type: data.type === e.target.value ? null : e.target.value })} buttonStyle='solid'> {types} </Group>
          </FormItem>
          <FormItem icon={faCog} label='sector_form_parameters'>
            <Checkbox checked={data.pausesEnabled} onChange={() => setData({ ...data, pausesEnabled: !data.pausesEnabled })}>
              {t('institution.option.quota_per_pause')}
            </Checkbox>
          </FormItem>
          <FormItem style={{ margin: '0px' }} icon={faRecycle} label='form.label.composition'>
            <div className='flex-row jc-between'>
              <LabelInputNumber
                className='mr-4'
                label={t('composition.beds')}
                value={data.composition?.beds ?? 0}
                onChange={v => setData({ ...data, composition: { ...data.composition, beds: v } })}
              />
              <LabelInputNumber
                className='mr-4'
                label={t('composition.rooms')}
                value={data.composition?.rooms ?? 0}
                onChange={v => setData({ ...data, composition: { ...data.composition, rooms: v } })}
              />
              <LabelInputNumber
                label={t('composition.boxes')}
                value={data.composition?.boxes ?? 0}
                onChange={v => setData({ ...data, composition: { ...data.composition, boxes: v } })}
              />
            </div>
          </FormItem>
        </div>
      </div>
      <Divider style={{ width: 'auto', margin: '12px 0px 12px 0px' }} type='horizontal' />
      <div className='w-100'>
        <StudentPathwaysBuffer defaultValues={data.studentPathways ?? []} onChange={p => setData({ ...data, studentPathways: p })} />
      </div>
      <Divider style={{ width: 'auto', margin: '12px 0px 0px 0px' }} type='horizontal' />
      <div className='flex-row mt-12'>
        {cancel && isFunction(onCancel) && <IconButton className='w-50' icon={faBan} onClick={onCancel} text={t('actions.cancel')} />}
        <IconButton className={cancel && isFunction(onCancel) ? 'w-50' : 'w-100'} type='primary' icon={faSave} onClick={handleSubmit} text={t('actions.save')} />
      </div>
    </div>
  )
}

export default SectorForm
